import { Typography, useEventCallback } from '@mui/material';
import { useFormikContext } from 'formik';
import {
  BodyLotsManagementVM,
  LotsManagementVM,
  TableLotsManagementVM,
} from 'front-office/view-models/lots-management/list';
import { Fragment, memo, useEffect, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { headCells } from 'front-office/constants/lots-management-list';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';

interface TableLotsManagementProps {
  valuesList?: TableLotsManagementVM;
}

const TableLotsManagementComponent = ({
  valuesList,
}: TableLotsManagementProps): React.ReactElement => {
  const formContext = useFormikContext<LotsManagementVM>();

  const navigate = useNavigate();

  useEffect(() => {
    if (valuesList) {
      formContext.setFieldValue('table', valuesList);
    }
  }, [valuesList]);

  const body: Array<BodyLotsManagementVM> = useMemo(() => {
    if (formContext.values.table?.list) {
      return formContext.values.table?.list.map((item) => ({
        ...item,
        hidden: {
          edit: false,
          read: false,
          delete: true,
        },
      }));
    }

    return [];
  }, [formContext.values.table?.list]);

  const onChangePage = useEventCallback((page: number) => {
    formContext.setFieldValue('table', {
      ...(formContext.values?.table || {}),
      page: page || 0,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    formContext.setFieldValue('table', {
      ...(formContext.values?.table || {}),
      page: 0,
      rowsPerPage,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyLotsManagementVM): Promise<BodyLotsManagementVM> => {
      return new Promise((resolve) => {
        const date = (cell.dateTimeAssignment?.split(' ') || [])[0] || '';

        const hours = (cell.dateTimeAssignment?.split(' ') || [])[1] || '';

        const dataAssegnazione = DateTime.fromFormat(date, 'dd/MM/yyyy').toFormat('yyyyMMdd') || '';

        const oraAssegnazione = DateTime.fromFormat(hours, 'HH.mm.ss').toFormat('HHmmss') || '';

        navigate({
          pathname: '/sportello/gestione-lotti/dettaglio',
          search: `?${createSearchParams({
            dataAssegnazione,
            oraAssegnazione,
            ente: formContext.values.filter?.entity || '',
            operatore: cell.operator || '',
            servizio: cell.service || '',
          })}`,
        });

        resolve(cell);
      });
    }
  );

  const onEditPress = useEventCallback(
    (cell: BodyLotsManagementVM): Promise<BodyLotsManagementVM> => {
      return new Promise((resolve) => {
        const date = (cell.dateTimeAssignment?.split(' ') || [])[0] || '';

        const hours = (cell.dateTimeAssignment?.split(' ') || [])[1] || '';

        const dataAssegnazione = DateTime.fromFormat(date, 'dd/MM/yyyy').toFormat('yyyyMMdd') || '';

        const oraAssegnazione = DateTime.fromFormat(hours, 'HH.mm.ss').toFormat('HHmmss') || '';

        navigate({
          pathname: '/sportello/gestione-lotti/modifica',
          search: `?${createSearchParams({
            dataAssegnazione,
            oraAssegnazione,
            ente: formContext.values.filter?.entity || '',
            operatore: cell.operator || '',
            servizio: cell.service || '',
          })}`,
        });
        resolve(cell);
      });
    }
  );

  const toPrintCSV = useMemo(() => {
    return (valuesList?.list || []) as unknown as Array<Record<string, string>>;
  }, [valuesList]);

  return (
    <Fragment>
      <div className="pmi-my-3">
        {body?.length > 0 ? (
          <CsvExtraction dataToExtract={toPrintCSV} title="Lista_Lotti_Carte_Identità" />
        ) : undefined}
      </div>
      <div className="pmi-my-3">
        {body?.length > 0 ? (
          <Table
            ariaLabel="Lista lotti carte d'identità"
            page={formContext.values.table?.page || 0}
            rowsPerPage={formContext.values.table?.rowsPerPage}
            body={body}
            count={body?.length}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            orders={{}}
            isNotLayoutFixed
            noSort
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )}
      </div>
    </Fragment>
  );
};

TableLotsManagementComponent.displayName = 'Lots Management List - Table - Form';

const TableLotsManagement = memo(TableLotsManagementComponent);

export default TableLotsManagement;
